import _ from 'lodash';
import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { getIsAdmin, getRoleConfig } from 'common-src/features/auth';
import { abortControllers } from 'common-src/features/rest';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';

import { AlertGroup, ModalGroup, RequestHandlerScreen } from 'src/components/base';
import { MainFooter, MainNavigation, TabsNavigation } from 'src/components/navigation';
import { AwsConnectDialer } from 'src/components/widgets';
import { CLIENT_ROOT } from 'src/constants/paths';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useSettingsPolling from 'src/hooks/useSettingsPolling';

import styles from './Dashboard.module.scss';
import { lazyRetry } from './helpers';

const DocumentationIndex = lazy(() => lazyRetry(() => import('src/pages/DocumentationIndex')));
const Settings = lazy(() => lazyRetry(() => import('src/pages/Settings')));
const PatientDetails = lazy(() => lazyRetry(() => import('src/pages/PatientDetails')));
const ManageClients = lazy(() => lazyRetry(() => import('src/pages/ManageClients')));
const Reports = lazy(() => lazyRetry(() => import('src/pages/Reports')));
const LeadCreation = lazy(() => lazyRetry(() => import('src/pages/LeadCreation')));
const Leads = lazy(() => lazyRetry(() => import('src/pages/Leads')));
const ManagePromptTemplates = lazy(() =>
  lazyRetry(() => import('src/pages/ManagePromptTemplates')),
);
const DashboardPage = lazy(() => lazyRetry(() => import('src/pages/DashboardPage')));
const CareOrchestration = lazy(() => lazyRetry(() => import('src/pages/CareOrchestration')));
const TaskTitleManager = lazy(() => lazyRetry(() => import('src/pages/TaskTitleManager')));

const ROUTES_WITHOUT_LEFT_NAVIGATION = [
  `${CLIENT_ROOT}/documentation/details/`,
  `${CLIENT_ROOT}/documentation/patient/`,
];

const Dashboard = () => {
  const location = useLocation();
  const isCareOrchestrationEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestration'),
  );
  const permissions = useCustomSelector(getRoleConfig);
  const isAdmin = useCustomSelector(getIsAdmin);

  const shouldShowTopNavigation = _.isEmpty(
    _.filter(ROUTES_WITHOUT_LEFT_NAVIGATION, (route) => location.pathname?.startsWith(route)),
  );

  const patientId = location?.pathname?.match(/\/[0-9]+\//g, '')?.[0]?.replaceAll('/', '');

  useSettingsPolling();

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      abortControllers.forEach((c) => c.abort());
    });
  }, []);

  const renderNavigation = () => {
    if (!shouldShowTopNavigation) return null;

    return (
      <nav>
        <TabsNavigation />
        <MainNavigation />
      </nav>
    );
  };

  const renderScreen = () => (
    <Suspense fallback={<RequestHandlerScreen isRequesting requestingText="Loading Perry" />}>
      <Routes>
        <Route
          path={`${CLIENT_ROOT}/patients/details/:patientId/*`}
          element={<PatientDetails key={`patient-details-${patientId || ''}`} />}
        />
        <Route
          path={`${CLIENT_ROOT}/documentation/details/:patientId/:documentationId/*`}
          element={<DocumentationIndex />}
        />
        <Route path={`${CLIENT_ROOT}/settings/*`} element={<Settings />} />
        {permissions.canManageClients && (
          <Route path={`${CLIENT_ROOT}/manage-clients`} element={<ManageClients />} />
        )}
        <Route
          path={`${CLIENT_ROOT}/manage-prompt-templates`}
          element={<ManagePromptTemplates />}
        />
        {isCareOrchestrationEnabled && (
          <Route path={`${CLIENT_ROOT}/care-orchestration/*`} element={<CareOrchestration />} />
        )}
        {isAdmin && <Route path={`${CLIENT_ROOT}/task-titles`} element={<TaskTitleManager />} />}
        <Route path={`${CLIENT_ROOT}/dashboard/*`} element={<DashboardPage />} />
        {permissions.canCreatePatient && (
          <>
            <Route path={`${CLIENT_ROOT}/leads`} element={<Leads />} />
            <Route path={`${CLIENT_ROOT}/lead-creation`} element={<LeadCreation />} />
          </>
        )}
        <Route path={`${CLIENT_ROOT}/*`} element={<div className={styles.empty} />} />
        {(permissions.canRequestReports || permissions.canRequestGuideReports) && (
          <Route path={`${CLIENT_ROOT}/reports`} element={<Reports />} />
        )}
      </Routes>
    </Suspense>
  );

  const renderFooter = () => {
    if (!shouldShowTopNavigation || location?.pathname?.includes('patients/details')) return null;

    return <MainFooter />;
  };

  return (
    <>
      {renderNavigation()}
      <main>{renderScreen()}</main>
      {renderFooter()}
      <ModalGroup />
      <AlertGroup />
      <AwsConnectDialer />
    </>
  );
};

export default Dashboard;
